import * as React from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface ToastProps {
	id: string;
	title: string;
	description?: string;
	variant?: "default" | "destructive" | "success" | "warning";
	isExiting?: boolean;
	onDismiss?: (id: string) => void;
}

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
	({ id, title, description, variant = "default", isExiting = false, onDismiss, ...props }, ref) => {
		const { t } = useTranslation();
		const getVariantClasses = () => {
			switch (variant) {
				case "destructive":
					return "border-destructive bg-destructive text-destructive-foreground";
				case "success":
					return "border-success/40 bg-card text-foreground";
				case "warning":
					return "border-warning/40 bg-card text-foreground";
				default:
					return "border-border bg-background text-foreground";
			}
		};

		return (
			<div
				ref={ref}
				data-state={isExiting ? "closed" : "open"}
				className={cn(
					"group pointer-events-auto relative flex w-full items-center justify-between overflow-hidden rounded-md border p-4 pe-14 shadow-lg z-50",
					"data-[state=open]:animate-in data-[state=open]:slide-in-from-top-full data-[state=open]:fade-in data-[state=open]:duration-300",
					"data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top-full data-[state=closed]:fade-out data-[state=closed]:duration-300",
					getVariantClasses(),
				)}
				{...props}
			>
				<div className="grid gap-1 text-sm">
					<div className="font-semibold">{title}</div>
					{description && <div className="text-sm opacity-90">{description}</div>}
				</div>

				{onDismiss && (
					<button
						onClick={() => onDismiss(id)}
						aria-label={t("common.close")}
						className="absolute end-2 top-2 flex h-11 w-11 items-center justify-center rounded-md text-current focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600"
					>
						<X className="h-4 w-4" />
					</button>
				)}
			</div>
		);
	},
);
Toast.displayName = "Toast";

export { Toast };
